import React, { ReactNode } from 'react';
import {
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  FormControl,
  FormControlProps,
  FormHelperTextProps,
  InputLabel,
  InputLabelProps,
  MenuItem,
  MenuItemProps,
} from '@mui/material';
import { Field, FieldProps } from 'react-final-form';

import {
  ErrorMessage,
  showErrorOnChange as showError,
  useFieldForErrors,
} from '@occc-app/design-system';
import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';

import { SelectData } from '../types';

interface SelectProps extends Partial<Omit<MuiSelectProps, 'onChange'>> {
  name: string;
  label?: ReactNode;
  required?: boolean;
  multiple?: boolean;
  helperText?: string;
  fieldProps?: Partial<FieldProps<any, any>>;
  formControlProps?: Partial<FormControlProps>;
  inputLabelProps?: Partial<InputLabelProps>;
  formHelperTextProps?: Partial<FormHelperTextProps>;
  menuItemProps?: Partial<MenuItemProps>;
  data?: SelectData[];
  children?: React.ReactElement | React.ReactElement[];
  onChange?: MuiSelectProps['onChange'];
  disabled?: boolean;
}

function MuiRffSelect(props: SelectProps) {
  const {
    name,
    label,
    data,
    children,
    required,
    multiple,
    helperText,
    fieldProps,
    inputLabelProps,
    formControlProps,
    formHelperTextProps,
    menuItemProps,
    onChange: onChangeCallBack,
    disabled,
    // labelWidth,
    ...restSelectProps
  } = props;
  const { isEditable } = useCanEditFormFieldsContext();

  if (!data && !children) {
    throw new Error('Please specify either children or data as an attribute.');
  }

  // This is for supporting the special case of variant="outlined"
  // Fixes: https://github.com/lookfirst/mui-rff/issues/91
  const { variant } = restSelectProps;
  const inputLabel = React.useRef<HTMLLabelElement>(null);

  const field = useFieldForErrors(name);
  const isError = showError(field);

  const isDisabled = disabled || !isEditable;

  return (
    <FormControl
      required={required}
      error={isError}
      fullWidth={true}
      variant={variant}
      {...formControlProps}
    >
      {!!label && (
        <InputLabel ref={inputLabel} htmlFor={name} disabled={isDisabled} {...inputLabelProps}>
          {label}
        </InputLabel>
      )}
      <Field
        name={name}
        render={({ input: { name, value, onChange, checked, ...restInput } }) => {
          // prevents an error that happens if you don't have initialValues defined in advance
          const finalValue = multiple && !value ? [] : value;

          return (
            <MuiSelect
              name={name}
              value={finalValue}
              onChange={(event, child) => {
                onChangeCallBack?.(event, child);
                onChange(event);
              }}
              multiple={multiple}
              label={label}
              disabled={isDisabled}
              inputProps={{ required, ...restInput }}
              {...restSelectProps}
            >
              {data
                ? data.map((item) => (
                    <MenuItem
                      value={item.value}
                      key={`${item.value}${item.label}`}
                      disabled={item.disabled}
                      {...(menuItemProps as any)}
                    >
                      {item.label}
                    </MenuItem>
                  ))
                : children}
            </MuiSelect>
          );
        }}
        {...fieldProps}
      />
      <ErrorMessage
        showError={isError}
        meta={field.meta}
        formHelperTextProps={formHelperTextProps}
        helperText={helperText}
      />
    </FormControl>
  );
}

export const Select: React.FC<SelectProps> = ({ formControlProps, ...props }) => (
  <MuiRffSelect
    color='primary'
    variant='outlined'
    formControlProps={{ size: 'medium', margin: 'none', fullWidth: true, ...formControlProps }}
    {...props}
  />
);
